import {AfterViewInit, Component, Input, OnInit} from '@angular/core';
import {
  Coverage,
  Duration,
  extractCoverageFromProtection,
  InsuranceCoverage,
  isWholeLife,
  JobRiskClass,
  Protection,
  RiderCoverage,
  RiderType,
  Sex,
  TextTranslations,
  Version,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CardModule} from 'primeng/card';
import {debounceTime, first, Subject} from 'rxjs';
import {ProposalPageOrchestrator} from '../../../services/proposal/proposal-page.orchestrator';
import {DEBOUNCE_TIME} from '../../../utils/utility-constants';
import {
  getSelectedCoverageAmount,
  getUpdatedLifeInsuranceCoverage,
  getUpdatedRiderCoverage,
} from './coverage-builder-utils';
import {TermLifeCardComponent} from './life-insurance/term-life/term-life-card.component';
import {WholeLifeCardComponent} from './life-insurance/whole-life/whole-life-card.component';
import {NeedsReminderSectionComponent} from './needs-reminder-section/needs-reminder-section.component';
import {CancerRiderCardComponent} from './rider/cancer-rider/cancer-rider-card.component';
import {ChildRiderCardComponent} from './rider/child-rider/child-rider-card.component';
import {DisabilityRiderCardComponent} from './rider/disability-rider/disability-rider-card.component';
import {SelectedProductSectionComponent} from './selected-product-section/selected-product-section.component';

export interface Product {
  name: TextTranslations;
  value: string;
  price?: number;
}

@UntilDestroy()
@Component({
  selector: 'em-coverage-builder-section',
  templateUrl: './coverage-builder-section.component.html',
  imports: [
    NeedsReminderSectionComponent,
    CardModule,
    SelectedProductSectionComponent,
    WholeLifeCardComponent,
    TermLifeCardComponent,
    ChildRiderCardComponent,
    CancerRiderCardComponent,
    DisabilityRiderCardComponent,
  ],
})
export class CoverageBuilderSectionComponent implements AfterViewInit, OnInit {
  private coverageUpdateSubject = new Subject<Coverage>();

  @Input({required: true}) set selectedProtection(_selectedProtection: Protection) {
    this._selectedProtection = _selectedProtection;
    this.selectedCoverage = extractCoverageFromProtection(this._selectedProtection);
    this.selectedWholeLifeCoverageAmount = getSelectedCoverageAmount(this.selectedCoverage, 'WHOLE');
    this.selectedTermLifeCoverageAmount = getSelectedCoverageAmount(this.selectedCoverage, 'TERM');
    this.selectedTermLifeDuration = Object.keys(this.selectedCoverage.lifeInsurances).find(
      (duration) => !isWholeLife(duration),
    ) as Duration;
    this.selectedWholeLifeDuration = Object.keys(this.selectedCoverage.lifeInsurances).find((duration) =>
      isWholeLife(duration),
    ) as Duration;

    const riders = this.selectedCoverage.riders;
    this.selectedChildRiderCoverageAmount = riders?.find((rider) => rider.type === RiderType.CHILD)?.coverage;
    this.selectedCancerRiderCoverageAmount = riders?.find(
      (rider) => rider.type === RiderType.CANCER,
    )?.coverage;
    const disabilityRider = riders?.find((rider) => rider.type === RiderType.DISABILITY);
    this.selectedDisabilityRiderCoverageAmount = disabilityRider?.coverage;
    this.selectedDisabilityRiderDuration = disabilityRider?.duration;
  }

  @Input({required: true}) ratesVersion!: Version;
  @Input({required: true}) age!: number;
  @Input({required: true}) smoker!: boolean;
  @Input({required: true}) sex!: Sex;
  @Input({required: true}) needs!: number;
  @Input({required: true}) completed!: boolean;
  @Input({required: true}) jobRiskClass: JobRiskClass | undefined;
  @Input() willRequireExtraRequirements: boolean | undefined;
  @Input() readonly = false;

  _selectedProtection!: Protection;
  selectedWholeLifeCoverageAmount: number | undefined;
  selectedTermLifeCoverageAmount: number | undefined;
  selectedTermLifeDuration: Duration | undefined;
  selectedWholeLifeDuration: Duration | undefined;
  selectedChildRiderCoverageAmount: number | undefined;
  selectedCancerRiderCoverageAmount: number | undefined;
  selectedDisabilityRiderCoverageAmount: number | undefined;
  selectedDisabilityRiderDuration: number | undefined;
  private selectedCoverage: Coverage = {lifeInsurances: {}};

  constructor(private readonly orchestrator: ProposalPageOrchestrator) {}

  ngOnInit(): void {
    this.coverageUpdateSubject
      .pipe(untilDestroyed(this), debounceTime(DEBOUNCE_TIME))
      .subscribe((coverage) => {
        this.orchestrator.update({coverage}).pipe(first()).subscribe();
      });
  }

  ngAfterViewInit(): void {
    if (
      this.completed == null ||
      this.needs == null ||
      this.age == null ||
      this.smoker == null ||
      !this.sex ||
      !this._selectedProtection ||
      !this.ratesVersion
    ) {
      throw new Error('Missing required input');
    }
  }

  updateLifeInsurance($event: InsuranceCoverage): void {
    const {amount, duration} = $event;
    this.selectedCoverage = getUpdatedLifeInsuranceCoverage(
      this.selectedCoverage,
      amount,
      duration,
      this.jobRiskClass,
    );

    this.coverageUpdateSubject.next(this.selectedCoverage);
  }

  updateRider($event: RiderCoverage): void {
    const {type, amount, duration} = $event;

    this.selectedCoverage = getUpdatedRiderCoverage(this.selectedCoverage, amount, duration, type);

    this.coverageUpdateSubject.next(this.selectedCoverage);
  }
}
